/* eslint-disable no-shadow */

import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/project-page`;

const state = {
  projectPages: null,
  pending: false,
  error: null,
  updatePending: false,
  updateError: null,
  totalCount: null,
  project: null,
};

const getters = {
  loaded: (state) => !state.pending && state.projectPages != null,
  projectPages: (state, getters) => (getters.loaded ? state.projectPages : []),
  noProjectPages: (state, getters) => getters.loaded && !getters.projectPages.length && state.totalCount === 0,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.projectPages = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, { projectPages, totalCount, project }) {
    state.pending = false;
    state.projectPages = projectPages;
    state.totalCount = totalCount;
    state.project = project;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state, projectPage) {
    state.projectPages.push(JSON.parse(JSON.stringify(projectPage)));
    state.updatePending = false;
    state.updateError = null;
  },
  modified(state, projectPage) {
    const idx = state.projectPages.findIndex((p) => p.id === projectPage.id);
    if (idx !== -1) {
      Vue.set(state.projectPages, idx, JSON.parse(JSON.stringify(projectPage)));
    }
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, pid) {
    const idx = state.projectPages.findIndex((projectPage) => projectPage.id === pid);
    if (idx !== -1) {
      state.projectPages.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      if (data.errors) {
        data.message = data.errors[Object.keys(data.errors)[0]];
      }
      err = new ValidationError(status, data.message, data.errors);
      break;
    default:
      break;
  }
  return err;
}

const actions = {
  async fetchData({ commit }, payload) {
    commit('beforeLoading');
    try {
      const result = await ApiService.get(`${endpoint}?currentPage=${payload.currentPage}&projectId=${payload.projectId}&q=${payload.q}`);
      const projectPages = result.data.data.items;
      const { project } = result.data.data;
      const totalCount = result.data.data.totalRows;
      commit('data', { projectPages, totalCount, project });
    } catch (error) {
      commit('error', error);
    }
  },
  async createProjectPage({ commit }, projectPage) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}`, { ...projectPage });
      commit('created', data);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateProjectPage({ commit }, projectPage) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${projectPage.id}`, { ...projectPage });
      commit('modified', { ...projectPage });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteProjectPage({ commit }, pid) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/${pid}`);
      commit('deleted', pid);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
